import * as styles from "./PlannedExpenseDialog.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, type RefObject, useMemo, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import {
  Field,
  FieldContent,
  FieldContext,
  FieldError,
  FieldLabel,
  makeFieldContext,
} from "@frontend/components/atoms/Field";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { useField } from "@frontend/hooks/form";
import { useDiscardGuard } from "@frontend/hooks/useDiscardGuard";
import type { InferFieldsFromSchema, ItemAtom } from "@frontend/lib/form";
import { DiscardChangesDialog } from "@frontend/shared/discard-changes-dialog";
import { ReferenceField } from "@frontend/shared/reference-field";
import { getAmountMask } from "@frontend/utils/masks";
import { Trans, useLingui } from "@lingui/react/macro";

import type {
  PlannedExpenseValues,
  plannedExpenseSchema,
} from "../schemas/project";
import {
  cancelExpenseEditAtom,
  editedExpenseAtom,
  optionsAtom,
  projectForm,
} from "../stores/editor";

export type PlannedExpenseFields = InferFieldsFromSchema<
  typeof plannedExpenseSchema
>;

const DIALOG_WIDTH = 480;

const gridFields = makeFieldContext({
  orientation: "vertical",
  grouped: false,
  errorDisplay: "tooltip",
});

export type PlannedExpenseDialogProps = {
  path: string;
  fields: PlannedExpenseFields;
  itemAtom: ItemAtom;
  created: boolean;
  onRemove: () => void;
  originRef?: RefObject<DOMRect | null>;
};

export function PlannedExpenseDialog({
  path,
  fields,
  itemAtom,
  created,
  onRemove,
  originRef,
}: PlannedExpenseDialogProps): JSX.Element {
  const { t } = useLingui();

  const options = useAtomValue(optionsAtom);
  const errors = useAtomValue(projectForm.state.errors);

  const close = useSetAtom(editedExpenseAtom);
  const cancelEdit = useSetAtom(cancelExpenseEditAtom);
  const touch = useSetAtom(projectForm.actions.touch);

  const quantity = useField(fields.quantity);
  const unitPrice = useField(fields.unitPrice);

  const current = useAtomValue(itemAtom) as PlannedExpenseValues;

  const [snapshot] = useState(current);

  const dirty = JSON.stringify(current) !== JSON.stringify(snapshot);

  const guard = useDiscardGuard({
    dirty,
    onClose: () => cancelEdit(snapshot),
  });

  const failing = useMemo(
    () => Object.keys(errors).filter((key) => key.startsWith(`${path}.`)),
    [errors, path],
  );

  const confirm = () => {
    if (0 < failing.length) {
      for (const key of failing) {
        touch(key);
      }

      return;
    }

    close(null);
  };

  const type = options.expenseTypes.find(({ id }) => id === current.typeId);

  return (
    <Dialog open onOpenChange={guard.onOpenChange} originRef={originRef}>
      <DialogContent size={DIALOG_WIDTH}>
        <DialogHeader>
          <DialogTitle>
            {created ? t`New planned expense` : t`Planned expense`}
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <div className={styles.body}>
          <FieldContext.Provider value={gridFields}>
            <div className={styles.grid}>
              <ReferenceField
                className={styles.span.full}
                label={t`Type`}
                placeholder={t`Please choose an option`}
                items={options.expenseTypes.map(({ id, label }) => ({
                  id,
                  label,
                  group: null,
                  available: null,
                }))}
                field={fields.typeId}
                onValueChange={(next) => {
                  // The type prices its own unit, which the row starts from.
                  const picked = options.expenseTypes.find(
                    ({ id }) => id === next,
                  );

                  if (picked?.unitCost && null == unitPrice.value) {
                    unitPrice.setValue(picked.unitCost);
                  }
                }}
              />

              <Field
                className={styles.span.half}
                invalid={Boolean(quantity.error)}
              >
                <FieldLabel>{t`Quantity`}</FieldLabel>

                <FieldContent>
                  <Input
                    type="number"
                    min={0}
                    value={null == quantity.value ? "" : String(quantity.value)}
                    rightSection={
                      type?.unitLabel ? (
                        <span className={styles.suffix}>{type.unitLabel}</span>
                      ) : undefined
                    }
                    onChange={(event) =>
                      quantity.setValue(
                        "" === event.target.value
                          ? null
                          : Number(event.target.value),
                      )
                    }
                  />

                  <FieldError match={Boolean(quantity.error)}>
                    {quantity.error}
                  </FieldError>
                </FieldContent>
              </Field>

              <Field
                className={styles.span.half}
                invalid={Boolean(unitPrice.error)}
              >
                <FieldLabel>{t`Unit price`}</FieldLabel>

                <FieldContent>
                  <Input
                    inputMode="decimal"
                    mask={getAmountMask()}
                    value={unitPrice.value ?? ""}
                    rightSection={
                      <span className={styles.suffix}>{t`Frs`}</span>
                    }
                    onValueChange={(next) =>
                      unitPrice.setValue("" === next ? null : next)
                    }
                  />

                  <FieldError match={Boolean(unitPrice.error)}>
                    {unitPrice.error}
                  </FieldError>
                </FieldContent>
              </Field>

              <ReferenceField
                className={styles.span.full}
                label={t`Recurrence`}
                placeholder={t`Please choose an option`}
                items={options.expenseRecurrences}
                field={fields.recurrenceId}
              />
            </div>
          </FieldContext.Provider>
        </div>

        <DialogFooter>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  className={styles.remove}
                  variant="ghost"
                  aria-label={t`Remove this expense`}
                  onClick={onRemove}
                >
                  <Icon name="Trash2" size="sm" />
                </Button>
              }
            />

            <TooltipContent>{t`Remove this expense`}</TooltipContent>
          </Tooltip>

          <Button variant="neutral" onClick={guard.requestClose}>
            <Trans>Cancel</Trans>
          </Button>

          <Button variant="primary" onClick={confirm}>
            <Trans>Apply</Trans>
          </Button>
        </DialogFooter>

        <DiscardChangesDialog
          open={guard.confirming}
          onKeep={guard.keep}
          onDiscard={guard.discard}
        />
      </DialogContent>
    </Dialog>
  );
}
